import React from 'react';

import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  cn,
} from '@crossnokaye/ux-common';

/** Number of shelf slots visible before scrolling is needed (icon equal-thirds layout). */
export const SHELF_VISIBLE_SLOTS = 3;

const SHELF_CAROUSEL_OPTS = {
  align: 'start' as const,
  containScroll: 'trimSnaps' as const,
  dragFree: false,
};

export type AnimaticShelfCarouselItem = {
  key: string;
  content: React.ReactNode;
};

type AnimaticShelfCarouselProps = {
  items: AnimaticShelfCarouselItem[];
  /** Equal thirds for icon shelves; content-sized slides for value shelves. */
  itemLayout?: 'equalThirds' | 'contentWidth';
  /** Overrides layout-default threshold for when prev/next controls appear. */
  paginationThreshold?: number;
  /** When set, overrides item-count threshold for control visibility. */
  showControls?: boolean;
  className?: string;
  frameClassName?: string;
};

const defaultPaginationThresholdByLayout: Record<
  NonNullable<AnimaticShelfCarouselProps['itemLayout']>,
  number
> = {
  equalThirds: SHELF_VISIBLE_SLOTS,
  contentWidth: SHELF_VISIBLE_SLOTS,
};

const carouselControlButtonClassName =
  'relative left-auto right-auto top-auto h-7 w-7 translate-x-0 translate-y-0 border-0 bg-background/80 shadow-sm';

const itemLayoutClasses: Record<NonNullable<AnimaticShelfCarouselProps['itemLayout']>, string> = {
  equalThirds: 'basis-1/3 min-w-0 shrink-0',
  contentWidth: 'basis-auto shrink-0 min-w-0',
};

/** Prevents shelf carousel interactions from bubbling to clickable parent cards. */
const stopCardClickBubble = (event: React.SyntheticEvent) => {
  event.stopPropagation();
};

type ShelfCarouselControlProps = {
  side: 'left' | 'right';
  children: React.ReactNode;
};

const ShelfCarouselControl = ({ side, children }: ShelfCarouselControlProps) => (
  <div
    className={cn('absolute top-1/2 z-10 -translate-y-1/2', side === 'left' ? 'left-0' : 'right-0')}
    onClick={stopCardClickBubble}
    onPointerDown={stopCardClickBubble}
    onMouseDown={stopCardClickBubble}
  >
    {children}
  </div>
);

export const AnimaticShelfCarousel = ({
  items,
  itemLayout = 'equalThirds',
  paginationThreshold,
  showControls: showControlsProp,
  className,
  frameClassName,
}: AnimaticShelfCarouselProps): React.ReactNode => {
  if (items.length === 0) {
    return null;
  }

  const resolvedThreshold = paginationThreshold ?? defaultPaginationThresholdByLayout[itemLayout];
  const showControls = showControlsProp ?? items.length > resolvedThreshold;

  return (
    <div
      className={cn(
        'relative w-full overflow-hidden rounded-md border-2 border-tertiary',
        frameClassName
      )}
      onClick={stopCardClickBubble}
      onPointerDown={stopCardClickBubble}
      onMouseDown={stopCardClickBubble}
    >
      <Carousel className={cn('w-full', className)} opts={SHELF_CAROUSEL_OPTS}>
        {showControls ? (
          <>
            <ShelfCarouselControl side="left">
              <CarouselPrevious
                className={carouselControlButtonClassName}
                size="icon"
                variant="ghost"
              />
            </ShelfCarouselControl>
            <ShelfCarouselControl side="right">
              <CarouselNext
                className={carouselControlButtonClassName}
                size="icon"
                variant="ghost"
              />
            </ShelfCarouselControl>
          </>
        ) : null}
        <CarouselContent className="-ml-0">
          {items.map((item, index) => (
            <CarouselItem
              key={item.key}
              className={cn(
                itemLayoutClasses[itemLayout],
                'pl-0',
                index < items.length - 1 && 'border-r-2 border-tertiary'
              )}
            >
              {item.content}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  );
};
